/**
 * Mock API for Client Credentials
 * This simulates an API call that returns merchant/store credentials
 */

import { UserProfileService } from "@services/UserProfileService";
import apiClient from "./apiClient";
import { EnvironmentMode } from "../contracts/application";

export interface MerchantCredential {
    id: string;
    storeId: string;
    merchantId: string;
    apiKey?: string;
    clientSecret?: string;
    environment: 'sandbox' | 'production';
    createdAt: string;
    lastUsed?: string;
    status: 'active' | 'inactive' | 'suspended';
}

export interface GetCredentialsResponse {
    data: MerchantCredential[];
    next: string | null;
    prev: string | null;
    self?: string;
    first?: string;
    last?: string;
}

// Mock data generator
const generateMockCredentials = (count: number = 15): MerchantCredential[] => {
    const credentials: MerchantCredential[] = [];
    
    for (let i = 1; i <= count; i++) {
        credentials.push({
            id: `cred-${i.toString().padStart(3, '0')}`,
            storeId: `store${(1000 + i).toString()}`,
            merchantId: `gwcan${(40000 + i).toString()}`,
            apiKey: `pk_test_${Math.random().toString(36).substring(2, 15)}${Math.random().toString(36).substring(2, 15)}`,
            clientSecret: `sk_test_${Math.random().toString(36).substring(2, 15)}${Math.random().toString(36).substring(2, 15)}`,
            environment: i % 3 === 0 ? 'production' : 'sandbox',
            createdAt: new Date(Date.now() - Math.random() * 90 * 24 * 60 * 60 * 1000).toISOString(),
            lastUsed: Math.random() > 0.3 ? new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString() : undefined,
            status: i % 10 === 0 ? 'inactive' : 'active'
        });
    }
    
    return credentials;
};

// Mock API delay
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Fetch merchant credentials with optional filtering
 * @param environment - Filter by environment (sandbox/production)
 * @param searchQuery - Search in storeId or merchantId
 * @param page - Page number for pagination
 * @param pageSize - Number of items per page
 */
export const fetchCredentials = async (
    email: string,
    environment?: 'sandbox' | 'production',
    searchQuery?: string,
    cursor: number = 0,
    limit: number = 10
): Promise<GetCredentialsResponse> => {
    const result = await UserProfileService.getUserProfiles(
        email,
        environment === 'sandbox' ? EnvironmentMode.sandbox : EnvironmentMode.production,
        cursor,
        limit,
        searchQuery
    );

    return {
        data: result.data || [],
        next: result.next || null,
        prev: result.prev || null,
        self: result.self,
        first: result.first,
        last: result.last,
    };
};

/**
 * Fetch a single credential by ID
 */
export const fetchCredentialById = async (id: string): Promise<MerchantCredential | null> => {
    await delay(300);
    
    const credentials = generateMockCredentials(15);
    return credentials.find(cred => cred.id === id) || null;
};

/**
 * Generate new credentials (mock creation)
 */
export const createCredential = async (
    environment: 'sandbox' | 'production'
): Promise<MerchantCredential> => {
    await delay(1000);
    
    const newId = `cred-${Date.now()}`;
    const randomNum = Math.floor(Math.random() * 9000) + 1000;
    
    return {
        id: newId,
        storeId: `store${randomNum}`,
        merchantId: `gwcan${40000 + randomNum}`,
        apiKey: `pk_${environment}_${Math.random().toString(36).substring(2, 15)}${Math.random().toString(36).substring(2, 15)}`,
        clientSecret: `sk_${environment}_${Math.random().toString(36).substring(2, 15)}${Math.random().toString(36).substring(2, 15)}`,
        environment,
        createdAt: new Date().toISOString(),
        status: 'active'
    };
};

/**
 * Revoke/delete credentials
 */
export const revokeCredential = async (id: string): Promise<boolean> => {
    await delay(800);
    
    // Simulate success
    return Math.random() > 0.1;
};
